/**
 * Debug Stream — client-side telemetry for the Apex toolbelt.
 *
 * Silent by default. Activates when the server signals debug_mode: true
 * (triggered by a toolbelt connecting to the debug WS). When active,
 * captures every DOM interaction, function flow trace, and API call,
 * streaming them in real-time over the main WS to the server, which
 * relays to the debug WS hub (toolbelt).
 *
 * When debug_mode goes false, all listeners tear down — zero overhead.
 *
 * Server-side routing:
 *   debug+ → debug WS hub (toolbelt)
 *   warn+  → also console.warn/error (server logs)
 */

import { send as wsSend, on as wsOn } from './ws-client';
import { isLeader as isWsLeader } from './leader-election';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Per-tab session ID for correlating logs from the same browser tab. */
const SESSION_ID = Math.random().toString(36).slice(2, 8);

const INPUT_DEBOUNCE_MS = 300;

/** Frozen no-op handle — shared by all trace() calls when debug is off. */
const NOOP_HANDLE: Readonly<TraceHandle> = Object.freeze({
  ok() {},
  fail() {},
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TraceHandle {
  ok(result?: Record<string, unknown>): void;
  fail(err: unknown): void;
}

interface LogEntry {
  action: 'client_log';
  ts: number;
  lvl: 'debug' | 'info' | 'warn' | 'error';
  mod: string;
  msg: string;
  page: string;
  sid: string;
  d?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let active = false;
let traceSeq = 0;
let cleanupFn: (() => void) | null = null;

/** Cross-tab relay — follower tabs post here; leader forwards to WS. */
const debugRelay =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('apex-debug-relay') : null;

/** Per-element input debounce timers. */
const inputTimers = new Map<EventTarget, ReturnType<typeof setTimeout>>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the debug stream. Call once from router.ts initApp().
 * Registers the debug_mode listener — actual capture starts/stops
 * when the server toggles debug mode.
 */
export function initDebugStream(): void {
  // Listen for system.debug_mode broadcasts from the server (via main WS)
  wsOn('system.debug_mode', (msg) => {
    const raw = msg as unknown as Record<string, unknown>;
    setActive(raw.enabled === true);
  });

  // Leader tab relays follower debug logs to the server
  if (debugRelay) {
    debugRelay.onmessage = (e: MessageEvent<LogEntry>) => {
      if (active && isWsLeader()) {
        wsSend(e.data as unknown as Record<string, unknown>);
      }
    };
  }

  // WS lifecycle tracing (via DOM events — no circular import)
  window.addEventListener('apex:ws-connected', () => {
    if (active) emit('info', 'ws', 'connected');
  });
  window.addEventListener('apex:ws-disconnected', () => {
    if (active) emit('warn', 'ws', 'disconnected');
  });
}

/**
 * Trace a function call. Returns { ok(), fail() } for marking completion.
 * When debug is off, returns a frozen no-op — zero cost, zero allocation.
 *
 * Usage:
 *   const t = trace('softphone', 'dial', { to: phone });
 *   try { const r = await doDial(); t.ok({ callId: r.id }); }
 *   catch (e) { t.fail(e); throw e; }
 */
export function trace(mod: string, fn: string, args?: Record<string, unknown>): TraceHandle {
  if (!active) return NOOP_HANDLE;
  const id = 'ct_' + (++traceSeq).toString(36);
  emit('debug', mod, `${fn} \u2192`, { trace_id: id, ...args });
  return {
    ok(result?: Record<string, unknown>) {
      emit('debug', mod, `${fn} \u2713`, { trace_id: id, ...result });
    },
    fail(err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      const stack =
        err instanceof Error ? err.stack?.split('\n').slice(0, 5).join('\n') : undefined;
      emit('warn', mod, `${fn} \u2717 ${errMsg}`, { trace_id: id, stack });
    },
  };
}

/**
 * Emit a one-shot log entry (no enter/exit pattern).
 * Use for events, state changes, and point-in-time observations.
 */
export function debugEmit(
  mod: string,
  msg: string,
  fields?: Record<string, unknown>,
  lvl: 'debug' | 'info' | 'warn' | 'error' = 'debug',
): void {
  if (!active) return;
  emit(lvl, mod, msg, fields);
}

// ---------------------------------------------------------------------------
// Activation / Deactivation
// ---------------------------------------------------------------------------

function setActive(enabled: boolean): void {
  if (enabled === active) return;
  active = enabled;
  if (enabled) {
    installDomCapture();
    emit('info', 'debug', 'client telemetry activated', { tab: SESSION_ID });
  } else {
    emit('info', 'debug', 'client telemetry deactivated');
    removeDomCapture();
  }
}

function installDomCapture(): void {
  removeDomCapture();

  // All listeners in capture phase — catches everything before stopPropagation
  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);
  document.addEventListener('submit', onSubmit, true);

  cleanupFn = () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('change', onChange, true);
    document.removeEventListener('input', onInput, true);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('submit', onSubmit, true);
    flushInputTimers();
  };
}

function removeDomCapture(): void {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = null;
  }
}

// ---------------------------------------------------------------------------
// DOM Handlers
// ---------------------------------------------------------------------------

function onClick(e: MouseEvent): void {
  const el = e.target;
  if (!(el instanceof Element)) return;
  const d: Record<string, unknown> = { el: descEl(el) };
  const text = elText(el);
  if (text) d.text = text;
  const action = el.getAttribute('data-action') || el.getAttribute('data-page');
  if (action) d.action = action;
  emit('debug', 'interaction', 'click', d);
}

function onChange(e: Event): void {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return;
  clearTimer(el);
  const d: Record<string, unknown> = { el: descEl(el) };
  if (!isSensitive(el)) {
    if (el instanceof HTMLSelectElement) {
      d.value = el.value;
      d.label = el.options[el.selectedIndex]?.text;
    } else if (el instanceof HTMLInputElement) {
      if (el.type === 'checkbox' || el.type === 'radio') {
        d.checked = el.checked;
      } else {
        d.value = el.value;
      }
    } else if (el instanceof HTMLTextAreaElement) {
      d.len = el.value.length;
    }
  } else {
    d.redacted = true;
  }
  emit('debug', 'interaction', 'change', d);
}

function onInput(e: Event): void {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return;
  clearTimer(el);
  inputTimers.set(
    el,
    setTimeout(() => {
      inputTimers.delete(el);
      emitInputValue(el);
    }, INPUT_DEBOUNCE_MS),
  );
}

function onFocusIn(e: FocusEvent): void {
  const el = e.target;
  if (!(el instanceof Element)) return;
  emit('debug', 'interaction', 'focus', { el: descEl(el) });
}

function onFocusOut(e: FocusEvent): void {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return;
  // Flush any pending input on blur
  if (inputTimers.has(el)) {
    clearTimer(el);
    emitInputValue(el);
  }
  emit('debug', 'interaction', 'blur', { el: descEl(el) });
}

function onSubmit(e: Event): void {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;
  const fields: string[] = [];
  for (const el of form.elements) {
    const name = (el as HTMLInputElement).name;
    if (name) fields.push(name);
  }
  emit('debug', 'interaction', 'submit', {
    el: descEl(form),
    action: form.action || undefined,
    method: form.method || 'get',
    fields: fields.length ? fields : undefined,
  });
}

// ---------------------------------------------------------------------------
// Emit / Transport
// ---------------------------------------------------------------------------

function emit(lvl: LogEntry['lvl'], mod: string, msg: string, d?: Record<string, unknown>): void {
  const entry: LogEntry = {
    action: 'client_log',
    ts: Date.now(),
    lvl,
    mod,
    msg,
    page: location.pathname,
    sid: SESSION_ID,
  };
  if (d) {
    const clean: Record<string, unknown> = {};
    let hasKeys = false;
    for (const [k, v] of Object.entries(d)) {
      if (v !== undefined) {
        clean[k] = v;
        hasKeys = true;
      }
    }
    if (hasKeys) entry.d = clean;
  }
  toServer(entry);
}

function toServer(entry: LogEntry): void {
  if (isWsLeader()) {
    wsSend(entry as unknown as Record<string, unknown>);
  } else if (debugRelay) {
    debugRelay.postMessage(entry);
  }
}

// ---------------------------------------------------------------------------
// Element Helpers
// ---------------------------------------------------------------------------

/** Build a concise CSS-like selector for an element. */
function descEl(el: Element): string {
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${el.id}`;

  const parts: string[] = [tag];

  const da = el.getAttribute('data-action') || el.getAttribute('data-page');
  if (da) {
    parts.push(`[data-action="${da}"]`);
    return parts.join('');
  }

  const name = el.getAttribute('name');
  if (name) parts.push(`[name="${name}"]`);

  const type = el.getAttribute('type');
  if (type && tag === 'input') parts.push(`[type="${type}"]`);

  if (el.classList.length > 0) {
    const cls = Array.from(el.classList).find(
      (c) => c.length > 2 && c.length < 40 && !c.startsWith('_'),
    );
    if (cls) parts.push(`.${cls}`);
  }

  return parts.join('');
}

/** Extract short text content from buttons, links, etc. */
function elText(el: Element): string | undefined {
  const t = (el.textContent || '').trim();
  if (!t || t.length > 60 || /^\d+$/.test(t)) return undefined;
  return t;
}

/** Check if an element contains sensitive data (passwords, credit cards). */
function isSensitive(el: HTMLElement): boolean {
  if (el instanceof HTMLInputElement && el.type === 'password') return true;
  if (el.hasAttribute('data-sensitive')) return true;
  if (el.closest('[data-sensitive]')) return true;
  const ac = el.getAttribute('autocomplete') || '';
  return ac.includes('cc-') || ac.includes('password');
}

/** Emit the current value of an input (debounce flush). */
function emitInputValue(el: HTMLElement): void {
  const d: Record<string, unknown> = { el: descEl(el) };
  if (!isSensitive(el)) {
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      const v = el.value;
      d.value = v.length > 200 ? v.slice(0, 200) + '\u2026' : v;
      d.len = v.length;
    }
  } else {
    d.redacted = true;
  }
  emit('debug', 'interaction', 'input', d);
}

function clearTimer(el: EventTarget): void {
  const t = inputTimers.get(el);
  if (t) {
    clearTimeout(t);
    inputTimers.delete(el);
  }
}

function flushInputTimers(): void {
  for (const [el, timer] of inputTimers) {
    clearTimeout(timer);
    if (el instanceof HTMLElement) emitInputValue(el);
  }
  inputTimers.clear();
}
